import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { AddressInformationMessageType } from "@redotech/redo-model/conversation/message-parsers/address-information-message-parser";
import {
  groupInput,
  input,
  InputProvider,
  nonEmptyValidator,
} from "@redotech/ui/form";
import classNames from "classnames";
import { FormEvent, memo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import { AutoCompleteInput } from "../../autocomplete-input";
import { Flex } from "../../flex";
import { Text } from "../../text";
import * as css from "./address-information-component.module.css";
import { ChatContentRenderer } from "./chat-content-renderer";
import {
  GenericConfirmedState,
  GenericRejectedState,
} from "./chat-content-states";
import { getConfirmedStateContent } from "./tool-message-utils";

const addressInformationForm = groupInput({
  address1: input<string>({
    validator: (value: string) => [...nonEmptyValidator(value)],
  }),
  address2: input<string | null>({}),
  city: input<string>({
    validator: (value: string) => [...nonEmptyValidator(value)],
  }),
  provinceCode: input<string>({
    validator: (value: string) => [...nonEmptyValidator(value)],
  }),
  zip: input<string>({
    validator: (value: string) => [...nonEmptyValidator(value)],
  }),
  countryCode: input<string | null>({}),
  firstName: input<string | null>({}),
  lastName: input<string | null>({}),
});

type AddressInformationValue = InputProvider.Value<
  typeof addressInformationForm
>;

// Type for autocomplete address fields
type AddressAutocompleteFields = {
  address1: string;
  city: string;
  province: string;
  zip: string;
  countryCode: string;
};

export interface AddressInformationData {
  address1?: string;
  address2?: string | null | undefined;
  city?: string | null | undefined;
  provinceCode?: string | null | undefined;
  zip?: string | null | undefined;
  countryCode?: string | null | undefined;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  orderNumber?: string;
}

export interface HandleAddressInformationFn {
  (params: {
    conversationId: string;
    pendingToolCallId: string;
    addressInfo: {
      address1: string;
      address2?: string | null;
      city?: string | null;
      provinceCode?: string | null;
      zip?: string | null;
      countryCode?: string | null;
      firstName: string;
      lastName: string;
    };
    cancelled?: boolean;
  }): Promise<void>;
}

export interface GetAddressSuggestionsFn {
  (
    query: string,
    sessionToken?: string,
  ): Promise<{ keyToFullObject: string; displayText: string }[]>;
}

export interface GetAddressFromSuggestionFn {
  (suggestionId: string): Promise<AddressAutocompleteFields | null>;
}

const AddressInformationPendingState = memo(
  function AddressInformationPendingState({
    message,
    addressInfo,
    readOnly,
    onAddressInformationSubmit,
    conversationId,
    toolMessageId,
    buttonText,
    messageSide = "left",
    getAddressSuggestions,
    getAddressFromSuggestion,
  }: {
    message: AddressInformationMessageType;
    addressInfo?: AddressInformationData;
    readOnly: boolean;
    onAddressInformationSubmit?: HandleAddressInformationFn | undefined;
    conversationId: string;
    toolMessageId: string;
    buttonText: string;
    messageSide?: "left" | "right";
    getAddressSuggestions?: GetAddressSuggestionsFn;
    getAddressFromSuggestion?: GetAddressFromSuggestionFn;
  }) {
    const [error, setError] = useState<string | undefined>(undefined);
    const [successfulAddress, setSuccessfulAddress] = useState<
      AddressInformationData | undefined
    >(
      message.customerInputContent?.confirmed
        ? message.customerInputContent?.addressInfo
        : undefined,
    );
    const [confirmedOrCancelled, setConfirmedOrCancelled] = useState(
      message.customerInputContent?.confirmed ??
        message.customerInputContent?.cancelled ??
        false,
    );
    const [cancelButtonPending, setCancelButtonPending] = useState(false);

    // Use customerInputContent.addressInfo if it exists, otherwise fall back to addressInfo prop
    // Always preserve orderNumber from the original addressInfo prop
    const effectiveAddressInfo = message.customerInputContent?.addressInfo
      ? {
          ...message.customerInputContent.addressInfo,
          orderNumber: addressInfo?.orderNumber,
        }
      : addressInfo;

    const initialValue: AddressInformationValue = {
      address1: effectiveAddressInfo?.address1 ?? "",
      address2: effectiveAddressInfo?.address2 ?? null,
      city: effectiveAddressInfo?.city ?? "",
      provinceCode: effectiveAddressInfo?.provinceCode ?? "",
      zip: effectiveAddressInfo?.zip ?? "",
      countryCode: effectiveAddressInfo?.countryCode ?? null,
      firstName: effectiveAddressInfo?.firstName ?? "",
      lastName: effectiveAddressInfo?.lastName ?? "",
    };
    const input = useInput(addressInformationForm, initialValue);

    const setAddressFields = useHandler(
      (address: AddressAutocompleteFields) => {
        input.setValue({
          ...input.value,
          address1: address.address1,
          city: address.city,
          provinceCode: address.province,
          zip: address.zip,
          countryCode: address.countryCode,
        });
      },
    );

    const [submitLoad, doSubmitLoad] = useTriggerLoad(async () => {
      if (onAddressInformationSubmit) {
        setConfirmedOrCancelled(true);
        await onAddressInformationSubmit({
          conversationId,
          pendingToolCallId: message.pendingToolCallId ?? "",
          addressInfo: {
            address1: input.value.address1,
            address2: input.value.address2,
            city: input.value.city,
            provinceCode: input.value.provinceCode,
            zip: input.value.zip,
            countryCode: input.value.countryCode,
            firstName: input.value.firstName ?? "",
            lastName: input.value.lastName ?? "",
          },
        });

        // Since the handler returns void, assume success if no error is thrown
        setError(undefined);
        setSuccessfulAddress({
          address1: input.value.address1 || addressInfo?.address1,
          address2: input.value.address2 || addressInfo?.address2,
          city: input.value.city || addressInfo?.city,
          provinceCode: input.value.provinceCode || addressInfo?.provinceCode,
          zip: input.value.zip || addressInfo?.zip,
          countryCode: input.value.countryCode || addressInfo?.countryCode,
          firstName: input.value.firstName || addressInfo?.firstName,
          lastName: input.value.lastName || addressInfo?.lastName,
        });
      }
    });

    const onFormSubmit = useHandler((event?: FormEvent<HTMLElement>) => {
      if (input.allErrors.length > 0) {
        return;
      }

      event?.preventDefault();
      doSubmitLoad();
    });

    const onCancelClick = useHandler(async () => {
      setCancelButtonPending(true);
      setConfirmedOrCancelled(true);
      setError(undefined);
      await onAddressInformationSubmit?.({
        conversationId,
        pendingToolCallId: message.pendingToolCallId ?? "",
        addressInfo: {
          address1: input.value.address1,
          firstName: input.value.firstName ?? "",
          lastName: input.value.lastName ?? "",
        },
        cancelled: true,
      });
    });

    if (successfulAddress && confirmedOrCancelled) {
      return (
        <Flex
          className={classNames(css.addressInformationBubbleContainer, {
            [css.messageSideLeft]: messageSide === "left",
            [css.messageSideRight]: messageSide === "right",
          })}
          dir="column"
          gap="md"
          px="lg"
          py="md"
        >
          <Text fontSize="md" fontWeight="semibold" textColor="primary">
            Address updated successfully
          </Text>
          <Text fontSize="sm" textColor="primary">
            {`${effectiveAddressInfo?.address1}`}
          </Text>
          {effectiveAddressInfo?.address2 && (
            <Text fontSize="sm" textColor="primary">
              {`${effectiveAddressInfo?.address2}`}
            </Text>
          )}
          <Text fontSize="sm" textColor="primary">
            {`${effectiveAddressInfo?.city}, ${effectiveAddressInfo?.provinceCode} ${effectiveAddressInfo?.zip}`}
          </Text>
          {effectiveAddressInfo?.firstName &&
            effectiveAddressInfo?.lastName && (
              <Text fontSize="sm" textColor="primary">
                {`${effectiveAddressInfo?.firstName} ${effectiveAddressInfo?.lastName}`}
              </Text>
            )}
        </Flex>
      );
    } else if (confirmedOrCancelled && !submitLoad.pending) {
      return (
        <Flex
          className={classNames(css.addressInformationBubbleContainer, {
            [css.messageSideLeft]: messageSide === "left",
            [css.messageSideRight]: messageSide === "right",
          })}
          dir="column"
          gap="md"
          px="lg"
          py="md"
        >
          <Text fontSize="md" fontWeight="semibold" textColor="primary">
            Cancelled update to shipping address
          </Text>
        </Flex>
      );
    }

    return (
      <Flex
        as="form"
        className={classNames(css.addressInformationBubbleContainer, {
          [css.messageSideLeft]: messageSide === "left",
          [css.messageSideRight]: messageSide === "right",
        })}
        dir="column"
        gap="md"
        onSubmit={onFormSubmit}
        px="lg"
        py="md"
      >
        {effectiveAddressInfo?.orderNumber && (
          <Flex className={css.headerSection} dir="column" gap="sm">
            <Flex
              className={css.orderNumberContainer}
              dir="row"
              gap="sm"
              w="fit-content"
            >
              <Text className={css.orderNumberLabel} fontSize="sm">
                {`Order #${effectiveAddressInfo.orderNumber}`}
              </Text>
            </Flex>
            <Text className={css.headerTitle} fontSize="md">
              Update shipping Information
            </Text>
          </Flex>
        )}
        {error && !submitLoad.pending && !confirmedOrCancelled && (
          <Text fontSize="sm" textColor="error">
            {error}
          </Text>
        )}
        <Flex dir="row" gap="md">
          <RedoTextInput
            label="First name"
            setValue={input.inputs.firstName.setValue}
            state={
              readOnly ||
              submitLoad.pending ||
              !onAddressInformationSubmit ||
              confirmedOrCancelled
                ? "disabled"
                : undefined
            }
            value={input.inputs.firstName.value ?? ""}
          />
          <RedoTextInput
            label="Last name"
            setValue={input.inputs.lastName.setValue}
            state={
              readOnly ||
              submitLoad.pending ||
              !onAddressInformationSubmit ||
              confirmedOrCancelled
                ? "disabled"
                : undefined
            }
            value={input.inputs.lastName.value ?? ""}
          />
        </Flex>
        {readOnly ||
        submitLoad.pending ||
        !onAddressInformationSubmit ||
        !getAddressSuggestions ||
        !getAddressFromSuggestion ||
        confirmedOrCancelled ? (
          <RedoTextInput
            label="Address"
            placeholder="Enter address"
            setValue={input.inputs.address1.setValue}
            state={
              readOnly ||
              submitLoad.pending ||
              !onAddressInformationSubmit ||
              confirmedOrCancelled
                ? "disabled"
                : undefined
            }
            value={input.value.address1}
          />
        ) : (
          <AutoCompleteInput<AddressAutocompleteFields, string>
            getSuggestions={getAddressSuggestions}
            label="Address"
            pickSuggestion={getAddressFromSuggestion}
            placeholder="Enter address"
            setAutocompleteFields={setAddressFields}
            setValue={(value) => {
              input.inputs.address1.setValue(value);
            }}
            value={input.value.address1}
          />
        )}
        <RedoTextInput
          label="Apartment, suite, etc. (optional)"
          placeholder="Enter apartment, suite, etc."
          setValue={input.inputs.address2.setValue}
          state={
            readOnly ||
            submitLoad.pending ||
            !onAddressInformationSubmit ||
            confirmedOrCancelled
              ? "disabled"
              : undefined
          }
          value={input.inputs.address2.value ?? ""}
        />
        <Flex dir="row" gap="md">
          <RedoTextInput
            label="City"
            placeholder="Enter city"
            setValue={input.inputs.city.setValue}
            state={
              readOnly ||
              submitLoad.pending ||
              !onAddressInformationSubmit ||
              confirmedOrCancelled
                ? "disabled"
                : undefined
            }
            value={input.inputs.city.value ?? ""}
          />
          <RedoTextInput
            label="State/Province"
            placeholder="Enter state or province"
            setValue={input.inputs.provinceCode.setValue}
            state={
              readOnly ||
              submitLoad.pending ||
              !onAddressInformationSubmit ||
              confirmedOrCancelled
                ? "disabled"
                : undefined
            }
            value={input.inputs.provinceCode.value ?? ""}
          />
        </Flex>
        <Flex dir="row" gap="md">
          <RedoTextInput
            label="ZIP/Postal Code"
            placeholder="Enter ZIP or postal code"
            setValue={input.inputs.zip.setValue}
            state={
              readOnly ||
              submitLoad.pending ||
              !onAddressInformationSubmit ||
              confirmedOrCancelled
                ? "disabled"
                : undefined
            }
            value={input.inputs.zip.value ?? ""}
          />
          <RedoTextInput
            label="Country Code"
            placeholder="Enter country code"
            setValue={input.inputs.countryCode.setValue}
            state={
              readOnly ||
              submitLoad.pending ||
              !onAddressInformationSubmit ||
              confirmedOrCancelled
                ? "disabled"
                : undefined
            }
            value={input.inputs.countryCode.value ?? ""}
          />
        </Flex>
        {error && !submitLoad.pending && (
          <Text fontSize="sm" textColor="error">
            {error}
          </Text>
        )}
        <Flex dir="row" gap="md" w="full">
          <RedoButton
            className={css.cancelButton}
            disabled={
              readOnly ||
              submitLoad.pending ||
              cancelButtonPending ||
              confirmedOrCancelled ||
              !onAddressInformationSubmit
            }
            hierarchy="secondary"
            onClick={onCancelClick}
            pending={cancelButtonPending}
            size="sm"
            text="Cancel"
          />
          <RedoButton
            className={css.submitButton}
            disabled={
              readOnly ||
              submitLoad.pending ||
              cancelButtonPending ||
              confirmedOrCancelled ||
              !onAddressInformationSubmit ||
              input.allErrors.length > 0
            }
            hierarchy="primary"
            onClick={() => onFormSubmit()}
            pending={submitLoad.pending}
            size="sm"
            text={buttonText}
          />
        </Flex>
      </Flex>
    );
  },
);

export const AddressInformationComponent = memo(
  function AddressInformationComponent(props: {
    message: AddressInformationMessageType;
    addressInfo?: AddressInformationData;
    readOnly: boolean;
    onAddressInformationSubmit?: HandleAddressInformationFn | undefined;
    conversationId: string;
    toolMessageId: string;
    buttonText: string;
    messageSide?: "left" | "right";
    getAddressSuggestions?: GetAddressSuggestionsFn;
    getAddressFromSuggestion?: GetAddressFromSuggestionFn;
  }) {
    const {
      message,
      addressInfo,
      readOnly,
      onAddressInformationSubmit,
      conversationId,
      toolMessageId,
      buttonText,
      messageSide,
      getAddressSuggestions,
      getAddressFromSuggestion,
    } = props;
    const effectiveAddressInfo = message.customerInputContent?.addressInfo
      ? {
          ...message.customerInputContent.addressInfo,
          orderNumber: addressInfo?.orderNumber,
        }
      : addressInfo;

    const { title: confirmedTitle, subtitle: confirmedSubtitle } =
      getConfirmedStateContent(
        message,
        "Address updated successfully",
        "Address not updated.",
        `${effectiveAddressInfo?.address1}${effectiveAddressInfo?.address2 ? `, ${effectiveAddressInfo.address2}` : ""}, ${effectiveAddressInfo?.city}, ${effectiveAddressInfo?.provinceCode} ${effectiveAddressInfo?.zip}`,
      );

    return (
      <ChatContentRenderer
        contentByState={{
          pending: (
            <AddressInformationPendingState
              addressInfo={addressInfo}
              buttonText={buttonText}
              conversationId={conversationId}
              getAddressFromSuggestion={getAddressFromSuggestion}
              getAddressSuggestions={getAddressSuggestions}
              message={message}
              messageSide={messageSide}
              onAddressInformationSubmit={onAddressInformationSubmit}
              readOnly={readOnly}
              toolMessageId={toolMessageId}
            />
          ),
          confirmed: (
            <GenericConfirmedState
              subtitle={confirmedSubtitle}
              title={confirmedTitle}
            />
          ),
          rejected: (
            <GenericRejectedState
              subtitle="Please try again or contact support for assistance"
              title="Unable to update address"
            />
          ),
        }}
        message={message}
      />
    );
  },
);
